/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { WorkerRequirementMappingLevel } from "@clipboard-health/contract-worker-app-bff";
import { API_TYPES } from "@src/appV2/api/constants";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { z } from "zod";

import {
  jobShiftSchema,
  PLACEMENT_STATUS,
  PlacementPerk,
  tagIncludeSchema,
  transparencyPayRatesSchema,
} from "../../Placements/types/fetchPlacements.schema";
import { INTERVIEW_TYPE } from "../../WorkerShifts/api/useFetchPaginatedInterviews";

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type { TagInclude } from "../../Placements/types/fetchPlacements.schema";

export const hiringFeedPlacementSchema = z.object({
  type: z.literal(API_TYPES.placement),
  id: z.string(),
  attributes: z.object({
    status: z.enum(PLACEMENT_STATUS),
    title: z.string(),
    description: z.string().optional(),
    jobShifts: z.array(jobShiftSchema),
    workerTypes: z.array(z.string()).optional(),
    perks: z.array(z.nativeEnum(PlacementPerk)).optional(),
    validUntil: z.coerce.date().optional(),
    createdAt: z.coerce.date(),
    originalJobLink: z.string().optional(),
    webApplicationLink: z.string().optional(),
    payRange: z
      .object({
        minPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
        maxPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
      })
      .optional(),
    signOnBonus: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .optional(),
    distance: z.number().optional(),
    address: z.object({
      formatted: z.string().optional(),
      stateCode: z.string().optional(),
    }),
    coordinates: z.object({
      latitude: z.number(),
      longitude: z.number(),
    }),
    interviewInstructions: z.string().optional(),

    promotion: z
      .object({
        type: z.enum(["featured"]),
      })
      .optional(),
    transparencyPayRates: transparencyPayRatesSchema.optional(),
  }),
  meta: z.object({
    isFavorited: z.boolean().optional(),
    isLicenseEligible: z.boolean(),
    isDocumentRequirementValid: z.boolean().optional(),
    isExemptFromDocumentRequirements: z.boolean().optional(),
    allowRequestInterview: z.boolean().optional(),
    scoreBreakdown: z
      .object({
        jobType: z.number(),
        shiftType: z.number(),
        pay: z.number(),
        days: z.number(),
        location: z.number(),
        verified: z.number(),
        status: z.number(),
        verifiedPay: z.number(),
        placementTier: z.number(),
      })
      .optional(),
    score: z.number().optional(),
    baseRate: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .optional(),
    totalRate: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .optional(),
    totalRateBreakdown: z
      .object({
        baseRate: z.number().optional(),
        benefitsDifferential: z.number().optional(),
        weekendDifferential: z.number().optional(),
        dayDifferential: z.number().optional(),
        eveningDifferential: z.number().optional(),
        nightDifferential: z.number().optional(),
      })
      .optional(),
    matchedType: z.enum(["exact", "discovery", "recentlyAdded"]).optional(),
    applicationMethod: z.enum([
      "instant_interview",
      "request_interview",
      "apply_in_app",
      "apply_in_web",
      "easy_scheduling",
    ]),
    isRecruiterScreeningEnabled: z.boolean().optional(),
    isEasyScheduling: z.boolean().optional(),
  }),
  relationships: z.object({
    workerFavoritePlacement: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.workerFavoritePlacement),
          id: z.string(),
        }),
      })
      .optional(),
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
    interviewSettings: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interviewSettings),
          id: z.string(),
        }),
      })
      .optional(),
    tags: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.tag),
            id: z.string(),
          })
        ),
      })
      .optional(),
  }),
});
export type HiringFeedPlacement = z.infer<typeof hiringFeedPlacementSchema>;

const interviewSocialProofWorkerSchema = z.object({
  firstName: z.string(),
  lastNameInitial: z.string(),
  profileImageUrl: z.string().optional(),
});

const interviewSocialProofSchema = z.object({
  totalCount: z.number(),
  workers: z.array(interviewSocialProofWorkerSchema),
});

export type InterviewSocialProof = z.infer<typeof interviewSocialProofSchema>;

export const workplaceIncludeSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    type: z.string(),
    address: z.object({
      state: z.string().optional(),
      stateCode: z.string().optional(),
      neighborhood: z.string().optional(),
    }),
    photos: z.array(z.string()).optional(),
    placementSettings: z.object({ isProspect: z.boolean().optional() }).optional(),
    rating: z
      .object({
        count: z.number(),
        value: z.number(),
      })
      .nullish(),
  }),
  relationships: z.object({
    workerRequirements: z.object({
      data: z.array(
        z.object({
          type: z.literal(API_TYPES.workerRequirement),
          id: z.string(),
        })
      ),
    }),
  }),
  meta: z
    .object({
      interviewDays: z.array(z.string()).optional(),
      interviewSocialProof: interviewSocialProofSchema.optional(),
    })
    .optional(),
});
export type WorkplaceInclude = z.infer<typeof workplaceIncludeSchema>;

export const interviewSettingsIncludeSchema = z.object({
  type: z.literal(API_TYPES.interviewSettings),
  id: z.string(),
  attributes: z.object({
    scheduleInterviews: z.boolean(),
    selfServeInterviewBookingType: z.nativeEnum(INTERVIEW_TYPE).optional(),
    availability: z.array(
      z.object({
        dayOfWeek: z.nativeEnum(DayOfTheWeek),
        start: z.number(),
        end: z.number(),
      })
    ),
  }),
});
export type InterviewSettingsInclude = z.infer<typeof interviewSettingsIncludeSchema>;

export const workerRequirementIncludeSchema = z.object({
  type: z.literal(API_TYPES.workerRequirement),
  id: z.string(),
  attributes: z.object({
    status: z.enum(["Rejected", "Expired", "Expiring Soon", "Missing", "In Review", "Approved"]),
    expiry: z.coerce.date().optional(),
    isValid: z.boolean(),
    qualifications: z.array(z.string()),
    requiredByWorkplacesCount: z.number().optional(),
    level: z.nativeEnum(WorkerRequirementMappingLevel),
  }),
  relationships: z.object({
    requirement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.requirement),
        id: z.string(),
      }),
    }),
  }),
});
export type WorkerRequirementInclude = z.infer<typeof workerRequirementIncludeSchema>;

export const requirementIncludeSchema = z.object({
  type: z.literal(API_TYPES.requirement),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    visibleToHCP: z.boolean().optional(),
  }),
});
export type RequirementInclude = z.infer<typeof requirementIncludeSchema>;

export const hiringEventIncludedSchema = z.object({
  type: z.literal(API_TYPES.hiringEvent),
  id: z.string(),
  attributes: z.object({
    type: z.enum(["OpenHouse", "JobFair"]),
    address: z.object({
      formatted: z.string().optional(),
    }),
    coordinates: z.object({
      latitude: z.number(),
      longitude: z.number(),
    }),
    fixedSchedule: z
      .object({
        startDate: z.string(),
        endDate: z.string(),
        interviewLength: z.number(),
        availableInterviewers: z.number(),
        overfillFirstSlots: z.boolean().optional(),
      })
      .optional(),
  }),
  relationships: z.object({
    workplaces: z.object({
      data: z.array(
        z.object({
          type: z.literal(API_TYPES.workplace),
          id: z.string(),
        })
      ),
    }),
  }),
});

export const hiringFeedsIncludeSchema = z.discriminatedUnion("type", [
  workplaceIncludeSchema,
  hiringEventIncludedSchema,
  interviewSettingsIncludeSchema,
  workerRequirementIncludeSchema,
  requirementIncludeSchema,
  tagIncludeSchema,
]);

export const hiringFeedsResponseSchema = z.object({
  data: z.array(hiringFeedPlacementSchema),
  included: z.array(hiringFeedsIncludeSchema),
  links: z.object({
    nextCursor: z.string().optional(),
    prevCursor: z.string().optional(),
  }),
  meta: z
    .object({
      availableStates: z.array(
        z.object({
          code: z.string(),
          name: z.string(),
        })
      ),
    })
    .optional(),
});
export type HiringFeedsResponse = z.infer<typeof hiringFeedsResponseSchema>;
